import React, { ReactNode } from 'react';
import { Button } from '@insurgrid/ui';
import { ReactComponent as DeviceIcon } from '../public/landingPage/device-icon.svg';
import { ReactComponent as IGIcon } from '../public/landingPage/IG-icon.svg';
import { ReactComponent as BusinessIcon } from '../public/landingPage/business-icon.svg';
import { ReactComponent as LockIcon } from '../public/landingPage/lock-icon.svg';
import { ReactComponent as ShieldIcon } from '../public/landingPage/shield-icon.svg';

export function IconItem(props: {
  icon: ReactNode;
  children: ReactNode;
  heading: string;
}) {
  return (
    <div className="flex items-center">
      <div>{props.icon}</div>
      <div className="ml-5 flex-col items-center">
        <p className="font-bold mb-0.5">{props.heading}</p>
        <div className="text-gray-500">{props.children}</div>
      </div>
    </div>
  );
}

export function LandingViewTemplate({
  buttonColorCode = '#3a71f8',
  iconTwoHeading = 'Your data is safe',
  iconTwoDescription = 'Transfer of your insurance information is encrypted from end-to-end',
  buttonText = 'Continue',
  ...props
}: {
  agencyName: string | null;
  titleUseCase: string;
  iconOneHeading: string;
  iconOneDescription: string;
  iconTwoHeading?: string;
  iconTwoDescription?: string;
  buttonColorCode?: string;
  onContinueClick?: () => void;
  children?: ReactNode;
  // Form-builder agents override the entire `{agencyName} uses InsurGrid…`
  // sentence with free-form copy. When unset, the legacy template renders.
  headlineOverride?: string | null;
  descriptionOverride?: string | null;
  buttonText?: string;
  /** Form-builder Branding tab can hide the device → IG → building
   *  decoration row above the headline. Default false — legacy /landing
   *  keeps the decoration since it never passes this prop. */
  hideDecoration?: boolean;
}) {
  return (
    <div
      data-test-id="landing-view"
      guided-tour-id="landing-view"
      className="px-4"
    >
      {!props.hideDecoration && (
        <div className="hidden sm:flex w-full justify-center">
          <div className="max-w-lg w-full">
            <div className="w-full flex items-center justify-between sm:py-6 mb-2 sm:mb-4 relative justify-self-center">
              <div className="px-1 bg-white z-10 mt-1.5 relative">
                <DeviceIcon alt="device" />
              </div>
              <div className="rounded-full shadow-xl flex items-center relative w-10 h-10 sm:w-14 sm:h-14">
                <IGIcon alt="logo" className="z-10" />
              </div>
              <div className="px-1 bg-white z-10 mt-1 relative">
                <BusinessIcon alt="business" style={{ objectFit: 'contain' }} />
              </div>
              <div
                className="w-full border-2 absolute"
                style={{ borderColor: '#afc4fb' }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="w-full flex justify-center mt-3 sm:mt-0 mb-3 md:mb-6">
        <div className="w-full sm:w-4/5 max-w-lg mb-5">
          {props.headlineOverride ? (
            <>
              <p className="text-center text-lg sm:text-xl font-normal">
                {props.headlineOverride}
              </p>
              {props.descriptionOverride && (
                <p className="text-center text-sm text-gray-500 mt-3 leading-relaxed">
                  {props.descriptionOverride}
                </p>
              )}
            </>
          ) : (
            <p className="text-center text-lg sm:text-xl font-normal">
              <span className="font-medium">{props.agencyName} </span>
              uses
              <span className="font-medium"> InsurGrid </span>
              to {props.titleUseCase}.
            </p>
          )}
        </div>
      </div>

      {/* `items-center` was previously here — fine for the default copy
          ("Connect effortlessly" / "Your data is safe") which is long
          enough to span most of the card width, but it visibly slid each
          bullet to dead-center when an agent set short Welcome-step
          overrides ("Test" / etc.). Dropping it lets each IconItem
          stretch to the parent width and keeps the icon flush-left
          regardless of heading length. */}
      <div className="sm:mt-0 space-y-5 flex flex-col justify-center">
        <IconItem
          icon={<LockIcon alt="lock" style={{ width: 20, height: 25 }} />}
          heading={props.iconOneHeading}
        >
          <p>{props.iconOneDescription}</p>
        </IconItem>
        <IconItem
          icon={<ShieldIcon alt="shield" style={{ width: 20, height: 25 }} />}
          heading={iconTwoHeading}
        >
          <p>{iconTwoDescription}</p>
        </IconItem>
        {props.onContinueClick && (
          <Button
            type="submit"
            className="max-w-lg"
            data-test-id="next-step"
            style={{ backgroundColor: buttonColorCode }}
            onClick={() => props.onContinueClick?.()}
            guided-tour-id="landing-view-continue"
          >
            <p className="font-medium">{buttonText}</p>
          </Button>
        )}
      </div>
      {props.children}
    </div>
  );
}
